import React, { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { ExternalLink, LocateFixed, MapPin, Maximize2, Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { practiceApi } from "../../lib/practiceApi";
import { MapLocationEditor } from "./MapLocationEditor";
import "./map.css";

const MapCanvas = lazy(() => import("./MapCanvas").then(module => ({ default: module.MapCanvas })));
const MAP_URL = process.env.REACT_APP_OSM_MAP_URL;

export const PracticeMap = ({ practice, onChanged, onEditAddress }) => {
  const [view, setView] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [flash, setFlash] = useState("");
  const load = useCallback(async () => {
    try { setView(await practiceApi(`/practices/${practice.id}/location`)); setError(""); }
    catch (err) { setError(err.message); }
  }, [practice.id]);
  useEffect(() => { load(); }, [load, practice.version, practice.site_id]);
  async function update(path, body) {
    const result = await practiceApi(`/practices/${practice.id}/location${path}`, {
      method: path ? "POST" : "PATCH", body: JSON.stringify({ ...body, version: view.version }) });
    setView(result); setError(""); setFlash("Posizione salvata nel fascicolo");
    await onChanged();
  }
  async function locate() {
    setBusy(true); setError(""); setFlash("");
    try { await update("/resolve", {}); }
    catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }
  const location = view?.location;
  const [lng, lat] = location?.coordinates || [];
  const outside = location ? `${MAP_URL}/?mlat=${lat}&mlon=${lng}#map=17/${lat}/${lng}` : "";
  return <section className="practice-section practice-location" data-testid="practice-map-section">
    <div className="practice-section-heading">
      <h2><MapPin size={18} />Posizione intervento</h2>
      <div className="practice-map-actions">
        <button type="button" className="ghost" disabled={!view || busy} data-testid="practice-map-edit-coordinates" onClick={() => setEditing(true)} title="Modifica coordinate"><Pencil size={14} />Coordinate</button>
        {location && <button type="button" className="ghost" data-testid="practice-map-expand" onClick={() => setExpanded(true)}><Maximize2 size={14} />Espandi mappa</button>}
      </div>
    </div>
    <p className="practice-map-address" data-testid="practice-map-address">{view?.address || "Indirizzo dell’intervento non indicato"}</p>
    {view?.address_source === "cantiere" && <small data-testid="practice-map-address-source">Indirizzo del cantiere collegato</small>}
    {view?.stale && <p className="practice-notice" role="alert" data-testid="practice-map-stale">L’indirizzo è cambiato: la posizione visualizzata è quella precedente.</p>}
    {location ? <Suspense fallback={<p data-testid="practice-map-loading">Caricamento mappa…</p>}><MapCanvas location={location} /></Suspense>
      : <div className="practice-map-empty" data-testid="practice-map-empty"><MapPin size={30} /><span>{view ? "Posizione non ancora localizzata" : "Caricamento posizione…"}</span>
        {view && <button type="button" className="primary" disabled={busy} data-testid="practice-map-locate" onClick={view.address ? locate : onEditAddress}>
          <LocateFixed size={15} />{busy ? "Localizzazione…" : view.address ? "Localizza indirizzo" : "Inserisci indirizzo"}
        </button>}
      </div>}
    {location && <div className="practice-map-footer">
      <span data-testid="practice-map-accuracy">{view.stale ? "Posizione da aggiornare" : location.source === "manuale" ? "Posizione impostata manualmente" : "Posizione indicativa dall’indirizzo"}</span>
      <div className="practice-map-actions">
        {view.address && <button type="button" className="ghost" disabled={busy} data-testid="practice-map-relocate" onClick={locate}><LocateFixed size={14} />{busy ? "Localizzazione…" : "Localizza dall’indirizzo"}</button>}
        <a href={outside} target="_blank" rel="noopener noreferrer" data-testid="practice-map-external">Apri in OpenStreetMap<ExternalLink size={14} /></a>
      </div>
    </div>}
    {error && <p className="error" role="alert" data-testid="practice-map-error">{error}<button type="button" className="ghost" data-testid="practice-map-retry" onClick={load}>Aggiorna</button></p>}
    {flash && <p className="practice-map-success" role="status" data-testid="practice-map-success">{flash}</p>}
    {editing && <MapLocationEditor location={location} onClose={() => setEditing(false)} onSave={body => update("", body)} />}
    <Dialog open={expanded} onOpenChange={setExpanded}>
      <DialogContent className="practice-map-dialog" data-testid="practice-map-dialog" closeTestId="practice-map-dialog-close">
        <DialogHeader><DialogTitle>Mappa dell’intervento</DialogTitle><DialogDescription data-testid="practice-map-dialog-address">{view?.address || "Posizione intervento"}{view?.stale ? " · posizione da aggiornare" : ""}</DialogDescription></DialogHeader>
        {expanded && location && <Suspense fallback={<p data-testid="practice-map-expanded-loading">Caricamento mappa…</p>}><MapCanvas location={location} expanded /></Suspense>}
      </DialogContent>
    </Dialog>
  </section>;
};